
import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { forkJoin } from 'rxjs';
import { GridsterConfig, GridsterItem, Gridster, GridType } from 'angular-gridster2';
import { TimerService } from '../services/timer/timer.service';
import { ApiService } from '../services/api/api.service';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { Router } from '@angular/router';
import { CountdownComponent } from 'ngx-countdown';
import { CommonModule } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { environment } from '../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { DashboardItemConfig } from '../models/dashboard.model';
import { SpoedAtpData } from '../models/business-data.model';
import { UrgentPaginator, StockPaginator } from '../models/paginator.model';

@Component({
  selector: 'app-spoed',
  templateUrl: './spoed.component.html',
  styleUrls: ['./spoed.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    Gridster,
    GridsterItem,
    NgxChartsModule
  ],
})

export class SpoedComponent implements OnInit, OnDestroy {
  public currentTime: Date = new Date();
  public options!: GridsterConfig;
  public dashboard!: Array<DashboardItemConfig>;
  public version: string = 'n/a';

  public AtpData: SpoedAtpData = { LastUrgent: 0, Urgent: '', UrgentPaginator: '', Bought: '', Amount: '', UrgentTime: 1, Remaining: '', CurrentRemaining: '', ConversionRate: 0, Stock: '', StockPaginator: '' };

  @ViewChild('countdown') countdown!: CountdownComponent;

  private destroy$ = new Subject<void>();
  private clockInterval: any;
  private scheduledTimeouts: any[] = [];

  constructor(
    public scheduler: TimerService,
    public apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router
  ) { }

  public ngOnInit() {
    if (environment.production) {
      this.apiService.getAppVersionFromNgSw().then(v => this.version = v || 'n/a');
    }

    this.initializeClock();

    this.options = {
      gridType: GridType.Fit,
      displayGrid: 'onDrag&Resize',
      minCols: 12,
      minRows: 6,
      maxCols: 24,
      margin: 10,
      outerMargin: true,
      fixedRowHeight: 100,
      draggable: {
        enabled: true,
        ignoreContentClass: 'gridster-item-content',
        dragHandleClass: 'drag-handler',
      },
      resizable: {
        enabled: true,
      }
    };

    this.dashboard = [
      { cols: 6, rows: 6, y: 0, x: 0, hasContent: true, contentType: 'OpenUrgent' },
      { cols: 6, rows: 6, y: 0, x: 6, hasContent: true, contentType: 'AmountUrgent' },
      { cols: 12, rows: 1, y: 6, x: 0, hasContent: true, contentType: 'ConversionUrgent' },
    ];

    this.scheduleUpdate(() => this.getAmount(), 120000, 600000);
    this.scheduleUpdate(() => this.getBought(), 120000, 600000);
    this.scheduleUpdate(() => this.getOpenUrgent(), 60000, 600000);
    this.scheduleUpdate(() => this.getOpenStock(), 300000, 600000);
  }

  public ngOnDestroy() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    this.scheduledTimeouts.forEach(timeout => clearTimeout(timeout));
    this.scheduledTimeouts = [];

    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeClock() {
    this.clockInterval = setInterval(() => {
      this.currentTime = new Date();
      this.cdr.markForCheck();
    }, 1000);
  }

  private scheduleUpdate(callback: () => void, successDelay: number, errorDelay: number) {
    const wrapped = () => {
      if (this.destroy$.closed) return;
      try {
        callback();
      } catch {
        const errorTimeoutId = setTimeout(wrapped, errorDelay);
        this.scheduledTimeouts.push(errorTimeoutId);
        return;
      }
      const successTimeoutId = setTimeout(wrapped, successDelay);
      this.scheduledTimeouts.push(successTimeoutId);
    };
    wrapped();
  }

  private substractDate(days: number) {
    const calDate = new Date();
    const currentDate = new Date();
    calDate.setDate(currentDate.getDate() - days);
    const parsedDate = calDate.getFullYear() + '-' + ('0' + (calDate.getMonth() + 1)).slice(-2) + '-' + ('0' + calDate.getDate()).slice(-2);
    const date = currentDate.getFullYear() + '-' + ('0' + (currentDate.getMonth() + 1)).slice(-2) + '-' + ('0' + currentDate.getDate()).slice(-2);
    return { CalDate: parsedDate, TodyDate: date };
  }

  private getOpenUrgent() {
    this.apiService.getValuations(100, 1, '[{"field":"created", "direction":"DESC"}]',
      '{"AND":[["ValuationType.name","eq","urgent"],["StatusType.name","eq","new"],["status","eq",true]]}',
      '["id", "VehicleSpecification-Vehicle-make", "VehicleSpecification-Vehicle-modelSpecific", "created", "isTreated"]', '')
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error fetching open urgent valuations:', error);
          return of({ data: [], paginator: { records: 0 } });
        })
      )
      .subscribe(result => {
        this.AtpData.Urgent = result.data;
        this.AtpData.UrgentPaginator = result.paginator;
        const treatedUrgent = result.data.filter((item: any) => item['isTreated'] === false);
        if (treatedUrgent.length > 0 && treatedUrgent[0]?.created?.date) {
          treatedUrgent.reverse();
          const time = new Date(treatedUrgent[0].created.date).getTime() - new Date().getTime() + (15 * 60 * 1000);
          this.AtpData.LastUrgent = time / 1000;
        } else {
          this.AtpData.LastUrgent = 0;
        }
      });
  }

  private getOpenStock() {
    this.apiService.getValuations(100, 1, '[{"field":"created", "direction":"DESC"}]',
      '{"AND":[["ValuationType.name","eq","regular"],["StatusType.name","eq","new"],["status","eq",true], ["Company.id", "neq", "5360"], ["Company.id", "neq", "153119"]]}',
      '["id", "Company-name", "VehicleSpecification-Vehicle-make", "VehicleSpecification-Vehicle-modelSpecific", "created"]', '["stock"]')
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error fetching open stock:', error);
          return of({ data: [], paginator: { records: 0 } });
        })
      )
      .subscribe(result => {
        this.AtpData.Stock = result.data;
        this.AtpData.StockPaginator = result.paginator;
      });
  }

  private getBought() {
    const date = this.substractDate(31);
    const boughtRealValue$ = this.apiService.getValuations(1, 1, '',
      JSON.stringify({ "AND": [["created", "gte", date.CalDate, "00:00:00"], ["created", "lte", date.TodyDate, "00:00:00"], ["ResultType.name", "eq", "bought"]] }), false, null)
      .pipe(catchError(error => {
        console.error('Error fetching bought real value:', error);
        return of({ data: [], paginator: { records: 0 } });
      }));
    const bought$ = this.apiService.getValuations(1, 1, '',
      JSON.stringify({ "AND": [["gvExpirationDate", "gte", date.CalDate, "00:00:00"], ["gvExpirationDate", "lt", date.TodyDate, "00:00:00"], ["ResultType.name", "eq", "bought"]] }), false, null)
      .pipe(catchError(error => {
        console.error('Error fetching bought:', error);
        return of({ data: [], paginator: { records: 0 } });
      }));
    const total$ = this.apiService.getValuations(1, 1, '',
      JSON.stringify({ "AND": [["gvExpirationDate", "gte", date.CalDate, "00:00:00"], ["gvExpirationDate", "lt", date.TodyDate, "00:00:00"], ['dgwAmount', 'gt', 0]] }), false, null)
      .pipe(catchError(error => {
        console.error('Error fetching total:', error);
        return of({ data: [], paginator: { records: 0 } });
      }));

    forkJoin([boughtRealValue$, bought$, total$]).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error in forkJoin:', error);
        return of([{ data: [], paginator: { records: 0 } }, { data: [], paginator: { records: 0 } }, { data: [], paginator: { records: 0 } }]);
      })
    ).subscribe(([boughtRealValue, bought, total]) => {
      this.AtpData.Bought = boughtRealValue.paginator.records;
      this.calConversionRate(bought.paginator.records, total.paginator.records);
    });
  }

  private getAmount() {
    const date = this.substractDate(0);
    this.apiService.getValuations(1, 1, '',
      '{"AND":[["created","gte","' + date.TodyDate + ' 00:00:00"],["ValuationType.name","eq","urgent"]]}', false, null)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error fetching amount:', error);
          return of({ data: [], paginator: { records: 0 } });
        })
      )
      .subscribe(result => {
        this.AtpData.Amount = result.paginator.records;
      });
  }

  private calConversionRate(bought: number, totalAmount: number) {
    if (totalAmount === 0 || totalAmount === null || totalAmount === undefined) {
      this.AtpData.ConversionRate = 0;
    } else {
      this.AtpData.ConversionRate = (bought / totalAmount) * 100;
    }
  }

  public getConversionUrgentCards() {
    return [
      { icon: 'fas fa-car', value: this.AtpData.Bought, label: 'Gekocht (afgelopen maand)' },
      { icon: 'fas fa-car', value: this.AtpData.Amount, label: 'Aantal spoedtaxaties vandaag' },
      {
        icon: 'fas fa-car',
        value: this.AtpData.ConversionRate !== null
          ? this.AtpData.ConversionRate.toFixed(3) + '%'
          : '',
        label: 'Conversie percentage'
      }
    ];
  }

  public logout(): void {
    this.authenticationService.logout();
  }
}
